import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { VideoItemList } from './VideoItemList'
import { Check } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import {
  modalAddTrainingWorkoutData,
  modalAddTrainingWorkoutIsVisible,
  selectedTrainingWorkoutType,
  selectedTrainingWorkoutData
} from '../../../redux/reducers/training-workout/training-workout.selectors'
import { Workout } from '../../../models/interfaces/workout.interface'
import { showMessage } from '../../../redux/reducers/snackbar/snackbar.actions'
import {
  addTrainingWorkout,
  addSubstituteTrainingWorkout,
  addSubstituteToParent,
  resetModalAddTrainingWorkout
} from '../../../redux/reducers/training-workout/training-workout.actions'
import api from '../../../services/api'
import i18n from '../../../i18n'
import { Button } from '../../Button'
import InfiniteScroll from 'react-infinite-scroller'
import { LoadingPage } from '../../LoadingPage'
import { SearchLabels } from '../../SearchLabels'
import { WorkoutProps } from '../../../pages/Workouts'
import { removeAccent } from '../../../utils'
import SearchIcon from '@mui/icons-material/Search'

export const ModalAddTrainingWorkout = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isVisible = useAppSelector(modalAddTrainingWorkoutIsVisible)
  const selectedTrainingWorkout = useAppSelector(selectedTrainingWorkoutData)
  const trainingWorkoutType = useAppSelector(selectedTrainingWorkoutType)
  const [recorded] = useState<string[]>([])
  const [workoutList, setWorkoutList] = useState<Workout[]>([])
  const [workoutResultList, setWorkoutResultList] = useState<Workout[]>([])
  const [workoutPagination, setWorkoutPagination] = useState<Workout[]>([])
  const [page, setPage] = useState(1)
  const [hasNext, setHasNext] = useState(true)
  const data = useAppSelector(modalAddTrainingWorkoutData)
  const [labels, setLabels] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [chosenLabels, setChosenLabels] = useState<string[]>([])
  const [workoutsSelected, setWorkoutsSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [substituteError, setSubstituteError] = useState<string | null>(null)

  const isSubstituteMode = Boolean(data?.parentTrainingWorkoutId)

  useEffect(() => {
    if (isVisible) {
      dataFetch().catch(console.log)
      if (isSubstituteMode) {
        setWorkoutsSelected([
          ...(data?.parentWorkoutId ? [data.parentWorkoutId] : []),
          ...(data?.existingSubstituteWorkoutIds ?? [])
        ])
      } else {
        checkWorkoutSelected()
      }
      setWorkoutPagination([])
      setChosenLabels([])
      setSearch('')
      setSubstituteError(null)
    }
  }, [isVisible])

  const dataFetch = async () => {
    try {
      setIsLoading(true)
      const { data } = await api.get('workouts/defaults')
      const labels = data.flatMap(
        (workout: WorkoutProps) => workout.labels.map((label: string) => label.charAt(0).toUpperCase() + label.slice(1))
      ).sort()
      const labelsUnique = labels.filter((item: string, index: number) => labels.indexOf(item) === index)
      setWorkoutList(data)
      setWorkoutResultList(data)
      setHasNext(true)
      setPage(1)
      setLabels(labelsUnique)
      setIsLoading(false)
    } catch {
      setIsLoading(false)
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  const checkWorkoutSelected = (workoutId?: string) => {
    const arrWorkoutSelect: any = []
    if (workoutId) {
      arrWorkoutSelect.push(workoutId)
    }
    selectedTrainingWorkout.forEach((obj) => {
      Object.entries(obj?.data).forEach(([key]) => {
        if (key === 'trainingsWorkouts') {
          obj.data?.trainingsWorkouts?.forEach((objtrainingWorkouts: any) => {
            arrWorkoutSelect.push(objtrainingWorkouts.workout.id)
          })
        } else if (key === 'workout') {
          arrWorkoutSelect.push(obj.data.workout.id)
        }
      })
    })
    setWorkoutsSelected(arrWorkoutSelect)
  }

  const onClose = () => {
    dispatch(resetModalAddTrainingWorkout())
    setSubstituteError(null)
  }

  const handleAddWorkout = async (workout: Workout) => {
    if (isSubstituteMode) {
      await handleAddSubstitute(workout)
      return
    }
    await dispatch(addTrainingWorkout({
      payload: {
        trainingId: String(data?.id),
        workoutId: workout.id
      },
      type: trainingWorkoutType
    }))
    checkWorkoutSelected(workout.id)
  }

  const handleAddSubstitute = async (workout: Workout) => {
    if (workout.id === data?.parentWorkoutId) {
      setSubstituteError(t('trainingWorkout.sameWorkoutError'))
      return
    }
    if (workoutsSelected.includes(workout.id)) {
      setSubstituteError(t('trainingWorkout.duplicateSubstituteError'))
      return
    }
    setSubstituteError(null)
    const result = await dispatch(addSubstituteTrainingWorkout({
      payload: {
        trainingId: String(data?.id),
        workoutId: workout.id,
        parentTrainingWorkoutId: String(data?.parentTrainingWorkoutId)
      },
      type: trainingWorkoutType
    }))
    if (addSubstituteTrainingWorkout.fulfilled.match(result)) {
      dispatch(addSubstituteToParent({
        parentId: String(data?.parentTrainingWorkoutId),
        substitute: result.payload.data
      }))
      setWorkoutsSelected(prev => [...prev, workout.id])
      dispatch(showMessage({ message: `${t('trainingWorkout.substituteAdded')}` }))
    } else {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  const handleSearchField = (searchValue: string, chosenLabels: string[]) => {
    setSearch(searchValue)
    if (!searchValue && chosenLabels.length === 0) {
      setWorkoutResultList(workoutList)
      setWorkoutPagination(workoutList)
      return
    }
    setWorkoutResultList([])
    const searchText = removeAccent(searchValue)
    const dataFound = workoutList
      .filter((workout) => {
        const name = removeAccent(workout.name)
        return name.includes(searchText)
      })
      .filter((workout) => {
        if (chosenLabels.length === 0) return true
        return chosenLabels.every((item: string) =>
          workout.labels
            .map((label: string) => removeAccent(label))
            .some((it: string) => it.includes(removeAccent(item))))
      })
    setWorkoutResultList(dataFound)
    setWorkoutPagination([])
    setHasNext(true)
    setPage(1)
  }

  const loadMore = () => {
    const sizePage = 10
    const result = [...workoutPagination, ...workoutResultList.slice((page - 1) * sizePage, page * sizePage)]
    setPage(page + 1)
    setWorkoutPagination(result)
    setHasNext(result.length !== workoutResultList.length)
  }

  const dialogTitle = isSubstituteMode
    ? t('trainingWorkout.addSubstituteWorkout')
    : t('trainingWorkout.addWorkout')

  return (
    <Dialog open={isVisible} onClose={onClose} fullWidth={true} maxWidth={'md'}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      {substituteError && (
        <Stack mx={3} mt={1}>
          <Alert severity="error">{substituteError}</Alert>
        </Stack>
      )}
      <Stack ml={2.5} mr={3.5} py={2}>
        <Grid container sx={{ flexDirection: { xs: 'column', md: 'row' } }} alignItems="stretch" justifyContent="flex-start" spacing={2}>
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              autoComplete="off"
              placeholder={`${t('search.title')}`}
              onChange={(event: any) => {
                handleSearchField(event.target.value, chosenLabels)
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <SearchLabels
              labels={labels}
              onChange={(selectValues: string[]) => {
                setChosenLabels(selectValues)
                handleSearchField(search, selectValues)
              }}
            />
          </Grid>
        </Grid>
      </Stack>
      <DialogContent>

        {isLoading && <LoadingPage key={0} />}
        {!isLoading && <InfiniteScroll
          useWindow={false}
          pageStart={0}
          hasMore={hasNext}
          key={page}
          loadMore={loadMore}
          loader={<LoadingPage key={0} removeHeight={true} />}
        >
          {workoutPagination.map((item, index) => (
            <VideoItemList
              key={`${page}_${index}`}
              workoutSelected={workoutsSelected.includes(item.id)}
              item={item}
              recorded={recorded}
              addWorkout={async () => await handleAddWorkout(item)}
            />
          ))}
        </InfiniteScroll>
        }
      </DialogContent>
      <DialogActions>
        <Grid container p={2}>
          {recorded.length > 0 &&
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }} >
                <Check color="success" />
                <Typography variant="body1">{i18n.t('general.keyWorkoutAdded', { count: recorded.length })}</Typography>
              </Box>
            </Grid>
          }
          <Stack width={'98%'} direction="row" spacing={2} justifyContent="end">
            <Button
              title={`${t('general.btn.dismiss')}`}
              color="secondary"
              onClick={onClose} />
          </Stack>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}
